/**
 * Cross-cutting accessors for `BlockLinkContent`.
 *
 * The discriminated union nests OG-card fields (title/description/imageFile/
 * siteName) inside each variant's payload. Code that doesn't care which
 * variant the link is — empty-block checks, recall thumbnails, search
 * indexing — uses these helpers instead of narrowing on every call site.
 *
 * Variant-specific reads (e.g. "Twitter author handle") still narrow on
 * `content.subtype` directly.
 *
 * Note on `imageFile` vs `imageUrl`:
 *   The canonical preview-image field is `imageFile: FileReference` —
 *   handlers download the OG image into a pending file at ingest, persist
 *   uploads it to R2, and `getLinkImageUrl` resolves the resulting
 *   `internal` ref to a `cdn.putty.me` URL. The string `imageUrl` slot is
 *   kept on every variant payload as a read-only fallback for legacy rows
 *   written before `imageFile` existed; new handlers should not write to
 *   it.
 */
import { resolveFileUrl } from "../file-ref";
/**
 * Pull the variant-specific payload object out of a link content. Each
 * variant nests its payload under a key that matches its `subtype`.
 *
 * Defensive against malformed / legacy rows:
 *  - If `subtype` is missing or unknown (e.g. a pre-discriminated-union
 *    legacy row, or a half-written ingestor error stub of the shape
 *    `{ url }`), we fall back to reading OG-style fields from the top
 *    level. This avoids the `payload.imageFile` crash on `getBlockClasses`
 *    when the stream contains a block whose ingestion errored out before
 *    the variant payload was populated.
 *  - If `subtype` matches but the nested payload object is missing, we
 *    return an empty object instead of `undefined`. Callers can keep
 *    using `payload.imageFile` etc. without optional chaining.
 */
export function getLinkPayload(content) {
    // Cast once at the boundary so we can safely read both the discriminated
    // and the legacy / partial shapes without per-case type acrobatics.
    const c = content;
    switch (c.subtype) {
        case "generic":
        case "dictionary":
        case "twitter":
        case "instagram":
        case "youtube":
        case "linkedin":
        case "googlemaps":
        case "spotify":
        case "shipping":
        case "gist":
        case "cloud_document":
        case "wikipedia":
        case "substack":
        case "github":
            return c[c.subtype] ?? legacyTopLevelFields(c);
        default:
            return legacyTopLevelFields(c);
    }
}
/**
 * Best-effort read of OG-style fields from the top of a link content
 * object. Used as a fallback when a row is missing its discriminated
 * payload (legacy flat shape, half-written error stub, or a subtype the
 * current build doesn't know about).
 */
function legacyTopLevelFields(c) {
    return {
        title: c.title,
        description: c.description,
        imageFile: c.imageFile,
        imageUrl: c.imageUrl,
        screenshotFile: c.screenshotFile,
        siteName: c.siteName,
    };
}
/**
 * Best-effort title for any link variant. Variants with a more meaningful
 * title source (e.g. dictionary headword) prefer that over the OG title.
 *
 * The variant-specific reads use optional chaining because a row whose
 * `subtype` says "twitter" might still be missing `content.twitter` when
 * the ingest pipeline errored out partway through writing it.
 */
export function getLinkTitle(content) {
    switch (content.subtype) {
        case "dictionary":
            return content.dictionary?.headword ?? content.dictionary?.title;
        case "twitter":
            return content.twitter?.author?.handle
                ? `@${content.twitter.author.handle}`
                : content.twitter?.title;
        case "instagram":
            return content.instagram?.author?.handle
                ? `@${content.instagram.author.handle}`
                : content.instagram?.title;
        case "linkedin":
            return content.linkedin?.fullName ?? content.linkedin?.title;
        case "googlemaps":
            return content.googlemaps?.placeName ?? content.googlemaps?.title;
        case "wikipedia":
            return content.wikipedia?.articleTitle ?? content.wikipedia?.title;
        default:
            return getLinkPayload(content).title;
    }
}
/** Best-effort description for any link variant. */
export function getLinkDescription(content) {
    switch (content.subtype) {
        case "dictionary":
            return content.dictionary?.definition;
        case "linkedin":
            return content.linkedin?.headline ?? content.linkedin?.description;
        case "googlemaps":
            return content.googlemaps?.placeDescription ?? content.googlemaps?.description;
        case "gist":
            return content.gist?.gistDescription ?? content.gist?.description;
        default:
            return getLinkPayload(content).description;
    }
}
/**
 * Best-effort preview image URL for any link variant. Resolves the
 * CDN-routed `imageFile` when present (the canonical field), otherwise
 * falls back to the legacy raw external `imageUrl` string for rows
 * written before `imageFile` existed.
 */
export function getLinkImageUrl(content) {
    const payload = getLinkPayload(content);
    if (payload.imageFile)
        return resolveFileUrl(payload.imageFile);
    return payload.imageUrl;
}
/**
 * Get the underlying `FileReference` for a link variant's preview image.
 * Returns `undefined` for legacy rows that only have a string `imageUrl`
 * — callers wanting CDN-or-external resolution should use
 * `getLinkImageUrl` instead.
 */
export function getLinkImageFile(content) {
    return getLinkPayload(content).imageFile;
}
/**
 * Pipeline-internal accessor: read the raw `imageFile` slot, including
 * the transient `pending` state that handlers stash between the analyze
 * and persist stages. The persist file walker uses this to detect
 * pending refs that need to be uploaded to R2.
 *
 * Outside the ingestor pipeline, prefer `getLinkImageFile` (which only
 * exposes the persisted shape).
 */
export function getLinkIngestorImageFile(content) {
    return getLinkPayload(content).imageFile;
}
/**
 * Handler convenience: cast the in-flight `IngestorFileReference`
 * returned by `helpers.maybeCreatePendingThumbnail` to the persisted
 * `FileReference` shape required by the variant payload schemas.
 *
 * The runtime value is identical; persist's file walker rewrites any
 * `pending` discriminator to `internal`/`external` before the row is
 * validated and saved. Handlers use this purely to satisfy the schema
 * type when assigning `imageFile` in an object literal.
 */
export function persistedImageFile(ref) {
    return ref;
}
/**
 * Mutator: assign the preview image `FileReference` on the active variant
 * payload.
 *
 * Accepts the wider `IngestorFileReference` (allowing `pending`) so
 * handlers can stash the result of `maybeCreatePendingThumbnail` here
 * directly. The persist stage walks every link block, finds any pending
 * `imageFile`, uploads the bytes, and calls this mutator again with a
 * clean `internal`/`external` ref before the row is validated and saved.
 *
 * This is the only place outside handlers that should write to
 * `imageFile`; everywhere else reads via `getLinkImageUrl` /
 * `getLinkImageFile`.
 */
export function setLinkImageFile(content, file) {
    // The schema field is typed `FileReference` (no pending) because that's
    // the persisted shape; pending is a transient pipeline state. Cast at
    // the boundary — persist guarantees a clean ref by the time the row is
    // saved.
    const ref = file;
    // Defensive read: if the variant payload is missing (malformed row /
    // half-written stub), skip rather than throwing inside a tight persist
    // loop. The caller still has the ref and the row is salvageable.
    const c = content;
    if (!c.subtype)
        return;
    const payload = c[c.subtype];
    if (!payload)
        return;
    payload.imageFile = ref;
}
/** Best-effort site name for any link variant. */
export function getLinkSiteName(content) {
    return getLinkPayload(content).siteName;
}
/**
 * Get the underlying `FileReference` for a link's rendered page screenshot,
 * or `undefined` for variants/rows that have none. Distinct from
 * `getLinkImageFile` (the OG preview image).
 */
export function getLinkScreenshotFile(content) {
    return getLinkPayload(content).screenshotFile;
}
/**
 * Pipeline-internal accessor for the screenshot slot, including the transient
 * `pending` state. Mirrors {@link getLinkIngestorImageFile}; the persist file
 * walker uses it to detect pending screenshots that need uploading to R2.
 */
export function getLinkIngestorScreenshotFile(content) {
    return getLinkPayload(content).screenshotFile;
}
/**
 * Mutator: assign the rendered screenshot `FileReference` on the active
 * variant payload. Mirrors {@link setLinkImageFile} — accepts the wider
 * `IngestorFileReference` so handlers can stash a pending ref; persist
 * rewrites it to a clean ref before the row is saved.
 */
export function setLinkScreenshotFile(content, file) {
    const ref = file;
    const c = content;
    if (!c.subtype)
        return;
    // `screenshotFile` only exists on the generic payload, so cast the indexed
    // access rather than relying on the union (which lacks the field elsewhere).
    const payload = c[c.subtype];
    if (!payload)
        return;
    payload.screenshotFile = ref;
}
/**
 * Handler convenience: cast an in-flight pending screenshot ref to the
 * persisted `FileReference` shape required by the variant payload schemas.
 * Mirrors {@link persistedImageFile}; persist rewrites the pending ref to a
 * clean `internal`/`external` ref before the row is saved.
 */
export function persistedScreenshotFile(ref) {
    return ref;
}
